import { Outlet, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { ClerkProvider, useAuth, useUser } from '@clerk/clerk-react';
import { AuthLayout } from './auth-layout';
import { Toaster } from '@/components/ui/toaster';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useStatsigUser } from '@statsig/react-bindings';
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing Publishable Key');
}

const localization = {
  createOrganization: {
    formButtonSubmit: 'Create Merchant',
    title: 'Create Merchant'
  },
  organizationList: {
    action__createOrganization: 'Create Merchant',
    titleWithoutPersonal: 'Choose a Merchant',
    createOrganization: 'Create Merchant'
  },
  navbar: {
    description: 'Manage your Merchants.',
    title: 'Merchants'
  },
  organizationSwitcher: {
    action__createOrganization: 'Create Merchant',
    notSelected: 'No merchant selected'
  }
};

const httpLink = new HttpLink({
  uri: `${import.meta.env.VITE_CONTEXTUAL_API_URL}/graphql`
});

const ApolloJwtWrapper = ({ children }: PropsWithChildren) => {
  const { getToken } = useAuth();
  const client = useMemo(() => {
    const buildHeaders = {
      'build-time': __BUILD_TIME__
    };
    const authMiddleware = setContext(
      async (_, { headers }: { headers?: Record<string, string> }) => {
        const token = await getToken({
          template: 'data'
        });

        if (!token) {
          return { headers };
        }

        return {
          headers: {
            ...headers,
            ...buildHeaders,
            authorization: `Bearer ${token}`
          }
        };
      }
    );

    return new ApolloClient({
      link: from([authMiddleware, httpLink]),
      cache: new InMemoryCache(),
      headers: {
        ...buildHeaders
      },
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network'
        }
      },
      devtools: {
        enabled: import.meta.env.DEV
      }
    });
  }, [getToken]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export function RootLayout() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const forceRedirectUrl = searchParams.get('next');

  const allowedRedirectOrigins = [
    'http://admin.localhost:5002',
    'admin.localhost:5002',
    'https://admin.shipfriendly.io'
  ];

  return (
    <ClerkProvider
      localization={localization}
      routerPush={(to) => {
        void navigate(to);
      }}
      routerReplace={(to) => {
        void navigate(to, { replace: true });
      }}
      signUpForceRedirectUrl={forceRedirectUrl}
      signInForceRedirectUrl={forceRedirectUrl}
      publishableKey={PUBLISHABLE_KEY}
      allowedRedirectOrigins={allowedRedirectOrigins}
    >
      <ApolloJwtWrapper>
        <Layout />
        <Toaster />
      </ApolloJwtWrapper>
    </ClerkProvider>
  );
}

function Layout() {
  const { user, isLoaded, isSignedIn } = useUser();
  const { updateUserAsync } = useStatsigUser();
  const matchesSignIn = useMatch('/sign-in/*');
  const matchesSignUp = useMatch('/sign-up/*');

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      void updateUserAsync({
        userID: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        custom: {
          name: user.fullName ?? ''
        }
      });
    }
  }, [user, isLoaded, isSignedIn]);

  if (matchesSignIn || matchesSignUp) return <AuthLayout />;

  return <Outlet />;
}
